import type { AdminSelf } from '@saay/api-client';
import { businessDayBounds, formatTimestamp, statusLabel, type LocalDate } from '@saay/formatting';
import { AccessDenied, Dialog, ErrorState, PageHeader, Section, SheetSection } from '../kit.js';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import type { AdminAuditEvent } from '../api.js';
import { auditActionLabel } from '../audit-actions.js';
import {
  AUDIT_ACTION_OPTIONS,
  AUDIT_RESOURCE_TYPE_OPTIONS,
  AuditEventDiff,
  AuditEventMeta,
  AuditResourceCell,
} from '../audit-display.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { AuditEntitySearch } from '../components/audit-entity-search.js';
import { AdminListTable } from '../components/admin-list-table.js';
import { DateFilterPill, FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { ListFooter, ListLoadMore } from '../components/list-footer.js';
import { ClearFiltersButton, ListEmpty, ListSkeleton } from '../components/list-state.js';
import { auditDomainsFor, canUse } from '../permissions.js';
import { type AuditFilters, listStatus, useAuditEvents, useRoleAssignments } from '../queries.js';

/**
 * `/audit`: the prototype «حسابرسی و دفترکل» (FTD §9.14) with its two tabs. «رویدادها» is the
 * append-only audit viewer (AC §18.12 R3): keyset pages (`-occurredAt`, the opaque cursor), the
 * domain filter limited to the domains the role reads (M-17), and the server-masked before/after
 * values. «دفترکل» arrives with the ledger reads.
 */

const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;

function readFilters(params: URLSearchParams, domains: readonly string[]): AuditFilters {
  const value = (key: string) => {
    const raw = params.get(key);
    return raw === null || raw.trim() === '' ? undefined : raw.trim();
  };
  const domain = value('domain');
  const from = value('from');
  const to = value('to');
  const action = value('action');
  const resourceType = value('resourceType');
  const resourceId = value('resourceId');
  const actorUserId = value('actorUserId');
  return {
    ...(domain !== undefined && domains.includes(domain) ? { domain } : {}),
    ...(action === undefined ? {} : { action }),
    ...(resourceType === undefined ? {} : { resourceType }),
    ...(resourceId === undefined ? {} : { resourceId }),
    ...(actorUserId === undefined ? {} : { actorUserId }),
    ...(from === undefined || !LOCAL_DATE.test(from)
      ? {}
      : { createdFrom: businessDayBounds(from).start }),
    ...(to === undefined || !LOCAL_DATE.test(to) ? {} : { createdTo: businessDayBounds(to).end }),
  };
}

function Actor({ actor }: { readonly actor: AdminAuditEvent['actor'] }) {
  if (actor.kind === 'SYSTEM') {
    return (
      <span className="saay-cell-main">
        <strong>سیستم</strong>
      </span>
    );
  }
  return (
    <span className="saay-cell-main">
      <strong>{actor.displayNameFa ?? 'بدون نام'}</strong>
      {actor.role === null ? null : <small>{statusLabel('fixedRole', actor.role)}</small>}
    </span>
  );
}

function EventDialog({
  event,
  onOpenChange,
}: {
  readonly event: AdminAuditEvent;
  readonly onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog
      eyebrow="رویداد حسابرسی"
      onOpenChange={onOpenChange}
      open
      size="wide"
      title={auditActionLabel(event.action)}
    >
      <div className="admin-sheet-stack">
        <SheetSection id="audit-event-meta" title="مشخصات رویداد">
          <AuditEventMeta event={event} />
        </SheetSection>
        <SheetSection id="audit-event-diff" title="قبل و بعد">
          <AuditEventDiff after={event.after} before={event.before} />
        </SheetSection>
      </div>
    </Dialog>
  );
}

function AuditEvents({ self }: { readonly self: AdminSelf }) {
  const [params, setParams] = useSearchParams();
  const domains = auditDomainsFor(self);
  const filters = readFilters(params, domains);
  const events = useAuditEvents(filters);
  const rows = events.data?.pages.flatMap((page) => page.items) ?? [];
  const [opened, setOpened] = useState<AdminAuditEvent | null>(null);
  const filtered = Object.keys(filters).length > 0;
  const assignments = useRoleAssignments({ status: 'ACTIVE' });
  const actorRows = assignments.data?.pages.flatMap((page) => page.items) ?? [];
  const actorOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    for (const row of actorRows) {
      if (seen.has(row.user.id)) continue;
      seen.add(row.user.id);
      options.push({
        label: row.user.displayNameFa ?? 'بدون نام',
        value: row.user.id,
      });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label, 'fa'));
  }, [actorRows]);

  function change(changes: Readonly<Record<string, string>>, clearAll = false): void {
    const next = new URLSearchParams(clearAll ? '' : params);
    if (clearAll && params.has('tab')) next.set('tab', params.get('tab') ?? '');
    for (const [key, value] of Object.entries(changes)) {
      if (value.trim() === '') next.delete(key);
      else next.set(key, value.trim());
    }
    setParams(next);
  }

  return (
    <>
      <FilterPillBar
        activeFilterCount={
          ['domain', 'action', 'resourceType', 'actorUserId'].filter(
            (key) => (params.get(key) ?? '').trim() !== '',
          ).length + ((params.get('from') ?? '') !== '' || (params.get('to') ?? '') !== '' ? 1 : 0)
        }
        label="فیلتر رویدادها"
        onClearAll={() => change({}, true)}
      >
        <AuditEntitySearch
          onResolved={(selection) => {
            if (selection === null) {
              change({ resourceId: '', resourceType: '' });
              return;
            }
            change({ resourceId: selection.resourceId, resourceType: selection.resourceType });
          }}
          resourceId={params.get('resourceId') ?? ''}
          resourceType={params.get('resourceType') ?? ''}
        />
        {domains.length > 1 ? (
          <FilterPill
            emptyLabel="همه حوزه‌ها"
            label="حوزه"
            onChange={(domain) => change({ domain })}
            options={domains.map((value) => ({ label: statusLabel('auditDomain', value), value }))}
            value={filters.domain ?? ''}
          />
        ) : null}
        <FilterPill
          emptyLabel="همه عملیات‌ها"
          label="عملیات"
          onChange={(action) => change({ action })}
          options={AUDIT_ACTION_OPTIONS}
          value={params.get('action') ?? ''}
        />
        <FilterPill
          emptyLabel="همه موارد"
          label="نوع مورد"
          onChange={(resourceType) => change({ resourceType })}
          options={AUDIT_RESOURCE_TYPE_OPTIONS}
          value={params.get('resourceType') ?? ''}
        />
        <FilterPill
          emptyLabel="همه انجام‌دهنده‌ها"
          label="انجام‌دهنده"
          onChange={(actorUserId) => change({ actorUserId })}
          options={actorOptions}
          value={params.get('actorUserId') ?? ''}
        />
        <DateFilterPill
          from={(params.get('from') ?? '') as LocalDate}
          namePrefix="audit"
          onApply={(from, to) => change({ from, to })}
          to={(params.get('to') ?? '') as LocalDate}
        />
      </FilterPillBar>
      {domains.length === 1 && domains[0] !== undefined ? (
        <p className="admin-muted">
          {`فقط رویدادهای حوزهٔ «${statusLabel('auditDomain', domains[0])}» برای نقش شما نمایش داده می‌شود.`}
        </p>
      ) : null}
      <Section id="audit-events" title="رویدادهای مدیریتی">
        <QueryState
          empty={
            <ListEmpty
              {...(filtered
                ? { action: <ClearFiltersButton onClick={() => change({}, true)} /> }
                : {})}
              title={filtered ? 'رویدادی با این فیلترها پیدا نشد' : 'هنوز رویدادی ثبت نشده است'}
            />
          }
          error={events.error}
          isEmpty={rows.length === 0}
          onRetry={() => void events.refetch()}
          skeleton={<ListSkeleton label="در حال بارگذاری رویدادها…" />}
          status={listStatus(events)}
        >
          <AdminListTable
            caption="رویدادهای حسابرسی"
            columns={[
              {
                header: 'زمان',
                id: 'time',
                pin: 'start',
                render: (row) => (
                  <time dateTime={row.occurredAt}>{formatTimestamp(row.occurredAt)}</time>
                ),
              },
              { header: 'انجام‌دهنده', id: 'actor', render: (row) => <Actor actor={row.actor} /> },
              {
                header: 'عملیات',
                id: 'action',
                render: (row) => {
                  return (
                    <span className="saay-cell-main">
                      <strong>{auditActionLabel(row.action)}</strong>
                      {row.domain === null ? null : (
                        <small>{statusLabel('auditDomain', row.domain)}</small>
                      )}
                    </span>
                  );
                },
              },
              {
                header: 'مورد',
                id: 'resource',
                render: (row) => (
                  <AuditResourceCell
                    displayName={row.resourceDisplayFa}
                    resourceId={row.resourceId}
                    resourceType={row.resourceType}
                  />
                ),
              },
              { header: 'دلیل', id: 'reason', render: (row) => row.reason ?? '—' },
            ]}
            mobileSummary={(row) => ({
              primary: auditActionLabel(row.action),
              secondary: formatTimestamp(row.occurredAt),
            })}
            onRowClick={(row) => setOpened(row)}
            rowKey={(row) => row.id}
            rows={rows}
          />
          {events.isFetchNextPageError ? (
            <ErrorState error={events.error} onRetry={() => void events.fetchNextPage()} />
          ) : null}
          <ListFooter
            pager={
              <ListLoadMore
                hasMore={events.hasNextPage}
                loading={events.isFetchingNextPage}
                onLoadMore={() => void events.fetchNextPage()}
              />
            }
            shown={rows.length}
            total={undefined}
          />
        </QueryState>
      </Section>
      {opened === null ? null : (
        <EventDialog
          event={opened}
          onOpenChange={(open) => {
            if (!open) setOpened(null);
          }}
        />
      )}
    </>
  );
}

function AuditView({ self }: { readonly self: AdminSelf }) {
  // One section until the ledger reads ship; a tab row with one tab is noise (W4).
  return (
    <>
      <PageHeader title="حسابرسی و دفترکل" />
      {canUse(self, 'listAdminAuditEvents') ? <AuditEvents self={self} /> : <AccessDenied />}
    </>
  );
}

export function AuditPage() {
  usePageTitle('حسابرسی و دفترکل');
  return <RequireArea area="rolesAuditLedger">{(self) => <AuditView self={self} />}</RequireArea>;
}
